import React from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    CircularProgress,
    Stack,
    FormControl,
    Autocomplete,
    TextField,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    useTheme,
    alpha,
} from "@mui/material";
import { getContactConfig } from "../../utils/contactConfig";

export const ENTITY_TYPES = ["Individual", "SoleTrader", "Company", "Family", "Trust"];

export interface ClientMappingEntry {
    mode: "new" | "existing" | "skip";
    clientName: string;
    entityType: string;
    existingClient: any | null;
    relationshipType: string;
}

export interface MappingContact {
    id: string;
    name: string;
    email: string;
    phone?: string;
    source?: string;
}

interface MapClientsStepProps {
    contacts: MappingContact[];
    clientMappings: Record<string, ClientMappingEntry>;
    updateMapping: (id: string, updates: Partial<ClientMappingEntry>) => void;
    allClients: any[];
    loadingClients: boolean;
    onRequestLoadClients?: () => void;
}

const MapClientsStep: React.FC<MapClientsStepProps> = ({
    contacts,
    clientMappings,
    updateMapping,
    allClients,
    loadingClients,
    onRequestLoadClients,
}) => {
    const theme = useTheme();

    return (
        <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                For each contact, choose which Client entity to link them to. Defaults to creating a new Individual client.
            </Typography>

            {contacts.map((contact) => {
                const mapping = clientMappings[contact.id];
                if (!mapping) return null;

                return (
                    <Paper
                        key={contact.id}
                        variant="outlined"
                        sx={{
                            p: 2,
                            mb: 1.5,
                            borderRadius: "10px",
                            borderColor:
                                mapping.mode === "skip"
                                    ? "divider"
                                    : mapping.mode === "existing" && mapping.existingClient
                                    ? theme.palette.success.light
                                    : alpha(theme.palette.primary.main, 0.4),
                            transition: "border-color 200ms",
                        }}
                    >
                        {/* Contact header */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                            <Box>
                                <Typography variant="subtitle2" fontWeight={600}>{contact.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {contact.email || "No email"}{contact.phone ? ` · ${contact.phone}` : ""}
                                </Typography>
                            </Box>
                            {contact.source && (
                                <Typography variant="caption" color="text.secondary" sx={{ bgcolor: "action.hover", px: 1, py: 0.25, borderRadius: 1 }}>
                                    {contact.source}
                                </Typography>
                            )}
                        </Box>

                        {/* Mode selector */}
                        <Box sx={{ display: "flex", gap: 0.75, mb: 1.5 }}>
                            <Button
                                size="small"
                                variant={mapping.mode === "new" ? "contained" : "outlined"}
                                onClick={() =>
                                    updateMapping(contact.id, {
                                        mode: "new",
                                        clientName: contact.name,
                                        entityType: "Individual",
                                        existingClient: null,
                                        relationshipType: getContactConfig("Individual").defaultRelationship,
                                    })
                                }
                                sx={{ fontSize: "0.72rem", py: 0.3 }}
                            >
                                New Client
                            </Button>
                            <Button
                                size="small"
                                variant={mapping.mode === "existing" ? "contained" : "outlined"}
                                onClick={() => {
                                    updateMapping(contact.id, {
                                        mode: "existing",
                                        existingClient: null,
                                        relationshipType: getContactConfig("Individual").defaultRelationship,
                                    });
                                    onRequestLoadClients?.();
                                }}
                                sx={{ fontSize: "0.72rem", py: 0.3 }}
                            >
                                Link Existing
                            </Button>
                            <Button
                                size="small"
                                variant={mapping.mode === "skip" ? "contained" : "outlined"}
                                onClick={() => updateMapping(contact.id, { mode: "skip" })}
                                sx={{
                                    fontSize: "0.72rem",
                                    py: 0.3,
                                    color: mapping.mode === "skip" ? "#fff" : "text.secondary",
                                }}
                            >
                                Skip
                            </Button>
                        </Box>

                        {/* New client fields */}
                        {mapping.mode === "new" && (
                            <Grid container spacing={1.25}>
                                <Grid item xs={7}>
                                    <TextField
                                        label="Client Name"
                                        size="small"
                                        fullWidth
                                        value={mapping.clientName}
                                        onChange={(e) => updateMapping(contact.id, { clientName: e.target.value })}
                                        sx={{ bgcolor: "background.paper" }}
                                    />
                                </Grid>
                                <Grid item xs={5}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Entity Type</InputLabel>
                                        <Select
                                            value={mapping.entityType}
                                            label="Entity Type"
                                            sx={{ bgcolor: "background.paper" }}
                                            onChange={(e) => {
                                                const et = e.target.value;
                                                updateMapping(contact.id, {
                                                    entityType: et,
                                                    relationshipType: getContactConfig(et).defaultRelationship,
                                                });
                                            }}
                                        >
                                            {ENTITY_TYPES.map((t) => (
                                                <MenuItem key={t} value={t}>{t}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Relationship Type</InputLabel>
                                        <Select
                                            value={mapping.relationshipType}
                                            label="Relationship Type"
                                            sx={{ bgcolor: "background.paper" }}
                                            onChange={(e) => updateMapping(contact.id, { relationshipType: e.target.value })}
                                        >
                                            {getContactConfig(mapping.entityType).relationships.map((r) => (
                                                <MenuItem key={r} value={r}>{r}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        )}

                        {/* Link existing client */}
                        {mapping.mode === "existing" && (
                            <Stack spacing={1.25}>
                                <Autocomplete
                                    options={allClients}
                                    loading={loadingClients}
                                    getOptionLabel={(opt) => opt.name || ""}
                                    isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                    value={mapping.existingClient}
                                    onChange={(_, val) => {
                                        updateMapping(contact.id, {
                                            existingClient: val,
                                            relationshipType: val
                                                ? getContactConfig((val as any).entityType).defaultRelationship
                                                : mapping.relationshipType,
                                        });
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Search or select client"
                                            size="small"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingClients ? <CircularProgress size={14} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    renderOption={(props, opt) => (
                                        <Box component="li" {...props} key={opt._id}>
                                            <Box>
                                                <Typography variant="body2">{opt.name}</Typography>
                                                {(opt as any).entityType && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {(opt as any).entityType}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                />
                                {mapping.existingClient && (
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Relationship Type</InputLabel>
                                        <Select
                                            value={mapping.relationshipType}
                                            label="Relationship Type"
                                            sx={{ bgcolor: "background.paper" }}
                                            onChange={(e) => updateMapping(contact.id, { relationshipType: e.target.value })}
                                        >
                                            {getContactConfig(mapping.existingClient?.entityType).relationships.map((r) => (
                                                <MenuItem key={r} value={r}>{r}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Stack>
                        )}
                    </Paper>
                );
            })}
        </>
    );
};

export default MapClientsStep;
